<template>
    <div data-component="LineRedemptionBox"
         class="redemption-box line-redemption-box">
        <div class="redemption-box__flex">
            <!-- + / - redeem section for regular tickets (not instance per ticket) -->

            <!-- Reduce redeemed line qty btn -->
            <button v-if="orderItem.isRedemptionAllowed()"
                    data-testid="unredeemOneButton"
                    class="btn btn-lg ml-0"
                    type="button"
                    :class="[orderItem.getRedeemButtonColorClass(currentBranch.ID)]"
                    :disabled="isUnredeemOneButtonDisabled"
                    @click.prevent="unredeemOne()">
                <span class="fa-solid fa-lg"
                      :class="[orderItem.unredemptionInProgress ? 'fa-arrows-rotate fa-spin' : 'fa-circle-minus']"></span>
            </button>

            <!-- Line redemption count -->
            <span class="btn btn-lg btn-static redemption-box__redemption_count"
                  data-testid="redemptionBoxRedemptionCount">
                {{ orderItem.redeemed }} / {{ orderItem.qty }}
            </span>

            <!-- Increase redeemed line qty btn -->
            <button v-if="orderItem.isRedemptionAllowed()"
                    data-testid="redeemOneButton"
                    class="btn btn-lg ml-0"
                    type="button"
                    :class="[orderItem.getRedeemButtonColorClass(currentBranch.ID)]"
                    :disabled="isRedeemOneButtonDisabled"
                    @click.prevent="redeemOne()">
                <span class="fa-solid fa-lg"
                      :class="[orderItem.redemptionInProgress ? 'fa-arrows-rotate fa-spin' : 'fa-circle-plus']"></span>
            </button>

            <!-- Redeem all btn -->
            <button v-if="orderItem.isRedemptionAllowed() && !orderItem.isMembershipPlan() && orderItem.qty > 0 && orderItem.redeemed < orderItem.qty"
                    data-testid="redeemAllRemainingOnLineButton"
                    class="btn btn-lg btn--redeem-all-line flex--grow-mobile"
                    :class="[orderItem.getRedeemButtonColorClass(currentBranch.ID)]"
                    :disabled="isRedeemAllButtonDisabled"
                    type="button"
                    @click.prevent="redeemAllRemainingOnLine()">
                <!-- TODO pluralize -->
                {{ (orderItem.qty === 1 ? l('ORDER_DETAILS.REDEEM_LINE') : l('ORDER_DETAILS.REDEEM_ALL')) }}
            </button>

            <!-- Unredeem all btn -->
            <button v-if="orderItem.isRedemptionAllowed() && !orderItem.isMembershipPlan() && orderItem.qty > 0 && orderItem.redeemed == orderItem.qty"
                    data-testid="unredeemAllRemainingOnLineButton"
                    class="btn btn-lg btn--redeem-all-line flex--grow-mobile"
                    :class="[orderItem.getRedeemButtonColorClass(currentBranch.ID)]"
                    :disabled="isUnredeemAllButtonDisabled"
                    type="button"
                    @click.prevent="unredeemAllOnLineCheck()">
                <!-- TODO pluralize -->
                {{ (orderItem.qty === 1 ? l('ORDER_DETAILS.UNREDEEM_LINE') : l('ORDER_DETAILS.UNREDEEM_ALL')) }}
            </button>
        </div>

        <!-- Redemption status messages for the whole line -->
        <div class="redemption-box__status_messages"
             data-testid="redemptionBoxStatusMessages">
            <div v-if="!orderItem.isRedemptionAllowed() && orderItem.redeemed > 0">
                <!-- If redemption buttons are not shown show if it has already been redeemed. -->
                <span class="indicator indicator-success">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_REDEEMED') }}
                </span>
            </div>
            <!-- Display an expired line as not redeemed as blank looks a bit out of place. -->
            <div v-if="!orderItem.isRedemptionAllowed() && orderItem.redeemed == 0">
                <span class="indicator indicator-danger">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_NOT_REDEEMED') }}
                </span>
            </div>
            <div v-if="!orderItem.redeemable">
                <span class="indicator indicator-danger">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_UNREDEEMABLE') }}
                </span>
            </div>
            <div v-if="orderItem.redemptionError">
                <span class="indicator indicator-danger">
                    {{ l('ORDER_DETAILS.TICKET_STATUS_REDEMPTION_ERROR') }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import { container } from '@/container';
import { mapState } from 'vuex';
import { redemptionEnabledForOrderLineCheck } from '@/epos/vue/pages/order-details/functions/redemptionFunctions';

export default {
    name: 'LineRedemptionBox',

    props: {
        /**
         * @type {OrderLine}
         */
        orderItem: {
            type: Object,
            required: true,
        },
    },

    computed: {
        ...mapState(['currentBranch']),

        isUnredeemAllButtonDisabled() {
            return !this.redemptionEnabledForOrderLine
                || this.orderItem.redemptionIsInProgress()
                || this.orderItem.redemptionError;
        },

        isUnredeemOneButtonDisabled() {
            return !this.redemptionEnabledForOrderLine
                || this.orderItem.qty < 0
                || this.orderItem.redeemed === 0
                || this.orderItem.redemptionIsInProgress()
                || this.orderItem.redemptionError;
        },

        isRedeemAllButtonDisabled() {
            return !this.redemptionEnabledForOrderLine
                || this.orderItem.redemptionIsInProgress()
                || this.orderItem.redemptionError;
        },

        isRedeemOneButtonDisabled() {
            return !this.redemptionEnabledForOrderLine
                || this.orderItem.qty < 0
                || this.orderItem.redeemed === this.orderItem.qty
                || this.orderItem.redemptionIsInProgress()
                || this.orderItem.redemptionError;
        },

        redemptionEnabledForOrderLine() {
            return redemptionEnabledForOrderLineCheck(this.orderItem);
        },
    },

    methods: {
        // These methods broadcast events that are detected and handled by OrderDetailCtrl.
        redeemOne() {
            container.eventBus.broadcast(
                EventNames.LINE_REDEMPTION_BOX_REDEEM_ONE,
                this.orderItem.ID
            );
        },

        unredeemOne() {
            container.eventBus.broadcast(
                EventNames.LINE_REDEMPTION_BOX_UNREDEEM_ONE,
                this.orderItem.ID
            );
        },

        redeemAllRemainingOnLine() {
            container.eventBus.broadcast(
                EventNames.LINE_REDEMPTION_BOX_REDEEM_ALL_REMAINING_ON_LINE,
                this.orderItem.ID
            );
        },

        unredeemAllOnLineCheck() {
            container.eventBus.broadcast(
                EventNames.LINE_REDEMPTION_BOX_UNREDEEM_ALL_ON_LINE_CHECK,
                this.orderItem.ID
            );
        },
    },
};
</script>
